import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client, { imgUrl, logoUrl } from "@/lib/glossyApi";
import MagazineLayout from "@/components/MagazineLayout";
import Markdown from "@/components/Markdown";
import { Reveal } from "@/components/Reveal";
import MiniAd from "@/components/MiniAd";
import Seo from "@/components/Seo";
import { Globe, Instagram, Phone, MapPin, Tag, Mail, Linkedin, Facebook, ShoppingBag } from "lucide-react";
import { igUrl, ttUrl, liUrl, fbUrl } from "@/lib/socialLinks";

const TikTokIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.9 2.9 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .58.04.86.13V9.4a6.33 6.33 0 0 0-1-.05A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
  </svg>
);

// One consistent, icon-only social row for EVERY member — no raw URLs, no text labels,
// fixed order, and an icon only appears when a usable link exists.
const socialLinks = (m) => {
  const out = [];
  const ig = igUrl(m.instagram);
  if (ig) out.push({ key: "instagram", label: "Instagram", icon: <Instagram size={15} />, href: ig });
  const tt = ttUrl(m.tiktok);
  if (tt) out.push({ key: "tiktok", label: "TikTok", icon: <TikTokIcon />, href: tt });
  const li = liUrl(m.linkedin);
  if (li) out.push({ key: "linkedin", label: "LinkedIn", icon: <Linkedin size={15} />, href: li });
  const fb = fbUrl(m.facebook);
  if (fb) out.push({ key: "facebook", label: "Facebook", icon: <Facebook size={15} />, href: fb });
  return out;
};

export default function MemberPage() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [more, setMore] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setM(null); setMore([]);
    client.get(`/members/${id}`).then((r) => { setM(r.data); document.title = `${r.data.business_name} — S/ME`; }).catch(() => setNotFound(true));
    client.get(`/articles?member_id=${id}&limit=20`).then((r) => setMore(r.data)).catch(() => {});
  }, [id]);

  if (notFound)
    return <MagazineLayout><div className="h-screen flex flex-col items-center justify-center gap-4"><p className="font-serif text-4xl">Member not found</p><Link to="/directory" className="overline link-underline">Back to Directory</Link></div></MagazineLayout>;

  if (!m) return <MagazineLayout><div className="h-screen flex items-center justify-center"><p className="overline text-ink/40">Loading…</p></div></MagazineLayout>;

  const pkg = m.package || {};
  const feature = pkg.feature;
  const advert = pkg.advert;
  const fiveQ = pkg.five_questions;

  return (
    <MagazineLayout darkNav>
      <Seo
        title={`${m.business_name} — S/ME`}
        description={m.bio}
        image={imgUrl(m)}
        type="profile"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: m.business_name,
          description: m.bio,
          image: imgUrl(m),
          address: { "@type": "PostalAddress", addressLocality: m.borough, addressCountry: "GB" },
          founder: m.owner_name,
          telephone: m.phone || undefined,
          url: m.website || undefined,
        }}
      />
      {/* Cover */}
      <header className="relative h-screen w-full overflow-hidden grain" data-testid="member-page">
        <img src={imgUrl({ cover_image: m.directory_image || m.cover_image, fallback_image: m.fallback_image })} alt={m.owner_name} className={`absolute inset-0 w-full h-full object-cover ${m.hero_fit === "center" ? "object-center" : m.hero_fit === "low" ? "object-[center_75%]" : "object-top"} animate-kenburns`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/60" />
        {/* publication mark — only when the image itself isn't already stamped with the masthead */}
        <div className="absolute top-0 left-0 right-0 z-20 pt-24 sm:pt-28 px-5 sm:px-8">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            {!m.directory_image ? <span className="font-serif text-cream text-3xl leading-none tracking-tight">S/ME</span> : <span />}
            <span className="overline text-gloss-gold">The Cover · {m.borough}{m.founding_member ? " · Founding Member" : ""}</span>
          </div>
        </div>
        <div className="absolute inset-0 flex items-end z-10">
          <div className="max-w-[1600px] mx-auto w-full px-5 sm:px-8 pb-20">
            <div className="flex items-center gap-3 mb-4">
              <p className="overline text-gloss-gold">{m.category} · {m.borough}</p>
              {m.accent_color && <span className="inline-block h-3 w-8" style={{ backgroundColor: m.accent_color }} data-testid="member-accent-bar" />}
            </div>
            <h1 className="font-serif text-cream text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight">{m.owner_name}</h1>
            <p className="font-grotesk text-cream/90 text-2xl sm:text-3xl mt-3 font-light">{m.business_name}</p>
          </div>
        </div>
      </header>

      {/* Profile / contact bar */}
      <section className="bg-ink text-cream px-5 sm:px-8 py-8 grain">
        <div className="max-w-[1600px] mx-auto relative z-10 flex flex-wrap items-center gap-x-10 gap-y-4">
          {logoUrl(m) && <img src={logoUrl(m)} alt={m.business_name} className="h-10 w-auto object-contain mr-2" data-testid="member-logo" />}
          {m.display_address !== false && m.address ? (
            <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider"><MapPin size={14} className="text-gloss-gold" /> {m.address}</span>
          ) : (
            <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider"><MapPin size={14} className="text-gloss-gold" /> {m.borough}</span>
          )}
          <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider"><Tag size={14} className="text-gloss-gold" /> {m.business_category || m.category}</span>
          {(() => {
            const norm = (u) => (u ? (/^https?:\/\//i.test(u) ? u : `https://${u}`) : null);
            const shop = norm(m.shop_url);
            const site = norm(m.website);
            const showSite = site && (!shop || site.replace(/\/+$/, "") !== shop.replace(/\/+$/, ""));
            return (<>
              {shop && <a href={shop} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider link-underline" data-testid="member-shop-link"><ShoppingBag size={14} className="text-gloss-gold" /> Shop</a>}
              {showSite && <a href={site} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider link-underline"><Globe size={14} className="text-gloss-gold" /> Website</a>}
            </>);
          })()}
          {m.display_phone !== false && m.mobile && <a href={`tel:${m.mobile}`} className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider link-underline"><Phone size={14} className="text-gloss-gold" /> {m.mobile}</a>}
          {m.display_phone !== false && m.use_landline && m.landline && <a href={`tel:${m.landline}`} className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider link-underline"><Phone size={14} className="text-gloss-gold" /> {m.landline}</a>}
          {m.display_phone !== false && !m.mobile && m.phone && <a href={`tel:${m.phone}`} className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider link-underline"><Phone size={14} className="text-gloss-gold" /> {m.phone}</a>}
          {m.email && <a href={`mailto:${m.email}`} className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider link-underline"><Mail size={14} className="text-gloss-gold" /> {m.email}</a>}
          <span className="flex items-center gap-2.5" data-testid="member-social-icons">
            {socialLinks(m).map((s) => (
              <a key={s.key} href={s.href} target="_blank" rel="noreferrer" title={s.label} aria-label={s.label}
                className="w-9 h-9 border border-cream/30 text-gloss-gold flex items-center justify-center hover:bg-gloss-gold hover:text-ink hover:border-gloss-gold transition-colors"
                data-testid={`member-social-${s.key}`}>
                {s.icon}
              </a>
            ))}
          </span>
        </div>
      </section>

      {/* Feature */}
      <section className="bg-cream py-16 sm:py-24 px-5 sm:px-8">
        <div className="max-w-[760px] mx-auto">
          {feature ? (
            <Reveal>
              <p className="overline text-gloss-silver mb-4">The Feature</p>
              <h2 className="font-serif text-3xl sm:text-5xl tracking-tight leading-[1.05] mb-3">{feature.title}</h2>
              <p className="font-sans text-xl text-ink/70 mb-10 leading-relaxed">{feature.dek}</p>
              <Markdown text={feature.body} />
            </Reveal>
          ) : (
            <Reveal>
              <p className="overline text-gloss-silver mb-4">About</p>
              <p className="font-sans text-xl leading-relaxed text-ink/80">{m.bio}</p>
            </Reveal>
          )}

          {m.offers && (
            <div className="mt-8 bg-ink text-cream p-7" data-testid="member-offer">
              <p className="overline text-gloss-gold mb-2">Reader Offer</p>
              <p className="font-serif text-2xl italic">{m.offers}</p>
            </div>
          )}
        </div>
      </section>

      {/* Advert */}
      {advert && (
        <section className="bg-ink text-cream py-20 px-5 sm:px-8">
          <div className="max-w-[900px] mx-auto text-center">
            <p className="overline mb-5">Advertisement</p>
            <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-none">{advert.headline}</h2>
            <p className="font-grotesk text-xl mt-4 font-light">{advert.subhead}</p>
            <p className="font-sans text-lg text-cream/85 mt-6 max-w-xl mx-auto leading-relaxed">{advert.body}</p>
            <span className="inline-block mt-8 border border-cream px-7 py-3 overline">{advert.cta}</span>
          </div>
        </section>
      )}

      {/* 5 Questions */}
      {fiveQ?.qa && (
        <section className="bg-cream py-20 px-5 sm:px-8">
          <div className="max-w-[760px] mx-auto">
            <p className="overline text-gloss-silver mb-3">The Interview</p>
            <h2 className="font-serif text-3xl sm:text-5xl tracking-tight mb-2">{fiveQ.title}</h2>
            {fiveQ.intro && <p className="font-sans text-lg text-ink/70 mb-10">{fiveQ.intro}</p>}
            <div className="divide-y divide-ink/15">
              {fiveQ.qa.map((item, i) => (
                <Reveal key={i} delay={i * 0.05} className="py-6">
                  <p className="font-serif text-xl sm:text-2xl italic mb-2">{i + 1}. {item.q}</p>
                  <p className="font-sans text-lg text-ink/80 leading-relaxed">{item.a}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* More from this member — every angle they appear in */}
      {more.filter((a) => a.type !== "member_feature").length > 0 && (
        <section className="bg-cream py-16 sm:py-24 px-5 sm:px-8 border-t border-ink/10" data-testid="more-from-member">
          <div className="max-w-[1600px] mx-auto">
            <p className="overline text-gloss-silver mb-2">More {m.business_name} in this issue</p>
            <h2 className="font-serif text-3xl sm:text-5xl tracking-tight mb-8">Seen across S/ME</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {more.filter((a) => a.type !== "member_feature").map((a, i) => (
                <Reveal key={a.id} delay={i * 0.05}>
                  <Link to={`/read/${a.slug}`} className="group block relative overflow-hidden bg-ink" data-testid="more-article-card">
                    <div className="relative aspect-[4/3] ed-frame">
                      <img src={imgUrl(a)} alt={a.title} className="w-full h-full object-cover ed-grade transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <p className="overline text-gloss-gold">{a.type === "spotlight" ? "Spotlight" : a.type === "five_questions" ? "The Interview" : a.type === "edit" ? `The Edit · ${a.category}` : a.category}</p>
                      <h3 className="font-serif text-xl sm:text-2xl text-cream tracking-tight leading-tight mt-2">{a.title}</h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* fixed member advert space */}
      <div className="bg-cream px-5 sm:px-8 py-8">
        <div className="max-w-[1280px] mx-auto"><MiniAd variant="banner" mode="image" space="member_page_banner" excludeMemberId={m.id} /></div>
      </div>

      <section className="bg-ink text-cream py-16 px-5 sm:px-8 grain text-center">
        <div className="relative z-10">
          <Link to="/directory" className="overline link-underline">← Explore the full directory</Link>
        </div>
      </section>
    </MagazineLayout>
  );
}
